import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { Fragment, useState } from 'react';

function not(a, b) {
  return a.filter(value => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a.filter(value => b.indexOf(value) !== -1);
}

function union(a, b) {
  return [...a, ...not(b, a)];
}

/**
 * Get available sources for a given data set and filter mapping.
 * @param {object} data key-value data object
 * @param {object} filterMap key-value to filter `data` as a key based on a value
 * @param {string} initial initial value if transfer list has a primary master
 * @returns {[string]} a list of keys from the data set
 */
function getAvailableSources(data, filterMap, initial = null) {
  if (!initial) {
    // If there is no initial value, present keys for entire data set.
    return Object.keys(data);
  }
  const keys = Object.keys(data).filter(key => key != initial);
  return !filterMap ? keys : keys.filter(key => filterMap[key] == filterMap[initial]);
}

export default function TransferList({
  initial,
  data,
  filterMap,
  hideSelect = false,
  noMatchText = 'No available sources.',
  getTooltip,
  getIsDisabled,
  renderRightIcon,
  onChangeMaster,
  onChangeTargets,
}) {
  const [checked, setChecked] = useState([]);
  const [master, setMaster] = useState(String(initial));
  const [left, setLeft] = useState(getAvailableSources(data, filterMap, initial));
  const [right, setRight] = useState([]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = value => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const numberOfChecked = items => intersection(checked, items).length;

  const handleChangeMaster = value => {
    setMaster(value);
    setLeft(getAvailableSources(data, filterMap, value));
    setRight([]);
    setChecked([]);

    if (onChangeMaster) {
      onChangeMaster(value);
    }

    if (onChangeTargets) {
      onChangeTargets([]);
    }
  };

  const handleToggleAll = items => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    }
  };

  const handleCheckedRight = () => {
    const newLeft = not(left, leftChecked);
    const newRight = right.concat(leftChecked);
    const newChecked = not(checked, leftChecked);

    setRight(newRight);
    setLeft(newLeft);
    setChecked(newChecked);

    if (onChangeTargets) {
      onChangeTargets(newRight);
    }
  };

  const handleCheckedLeft = () => {
    const newLeft = left.concat(rightChecked);
    const newRight = not(right, rightChecked);
    const newChecked = not(checked, rightChecked);

    setLeft(newLeft);
    setRight(newRight);
    setChecked(newChecked);

    if (onChangeTargets) {
      onChangeTargets(newRight);
    }
  };

  const customList = (title, items) => {
    return (
      <Card>
        <CardHeader
          sx={{ px: 2, py: 1 }}
          avatar={
            <Checkbox
              onClick={handleToggleAll(items)}
              checked={numberOfChecked(items) === items.length && items.length !== 0}
              indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
              disabled={items.length === 0}
              inputProps={{
                'aria-label': 'all items selected',
              }}
            />
          }
          title={title}
          subheader={`${numberOfChecked(items)}/${items.length} selected`}
        />
        <Divider />
        <List
          sx={{
            width: 200,
            height: 230,
            bgcolor: 'background.paper',
            overflow: 'auto',
          }}
          dense
          component="div"
          role="list"
        >
          {items.map(value => {
            const labelId = `transfer-list-all-item-${value}-label`;

            let isDisabled = false;
            if (getIsDisabled) {
              isDisabled = getIsDisabled(value);
            }

            let tooltip = null;
            if (getTooltip) {
              tooltip = getTooltip(value);
            }

            const listItem = (
              <ListItem
                key={value}
                role="listitem"
                button
                disabled={isDisabled}
                onClick={handleToggle(value)}
                sx={{ p: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <Checkbox
                    checked={checked.indexOf(value) !== -1}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={data[value]} />
                {renderRightIcon ? renderRightIcon(value) : null}
              </ListItem>
            );

            if (tooltip) {
              return (
                <Tooltip key={labelId} title={tooltip}>
                  {listItem}
                </Tooltip>
              );
            }

            return listItem;
          })}
          <ListItem />
        </List>
      </Card>
    );
  };

  return (
    <Fragment>
      {hideSelect ? null : (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="select-master-label" htmlFor="select-master">
            Master
          </InputLabel>
          <Select
            id="select-master"
            labelId="select-master-label"
            value={master}
            label="Master"
            onChange={e => handleChangeMaster(e.target.value)}
          >
            <MenuItem value={''}>None</MenuItem>
            {Object.keys(data).map(key => (
              <MenuItem key={key} value={key}>
                {data[key]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {(!hideSelect && !master) || (left.length === 0 && right.length === 0) ? (
        <Typography variant="caption" align="center" sx={{ flexGrow: 1, display: 'block' }}>
          {noMatchText}
        </Typography>
      ) : (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid>{customList('Choices', left)}</Grid>
          <Grid>
            <Grid container direction="column" alignItems="center">
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
              >
                &gt;
              </Button>
              <Button
                sx={{ my: 0.5 }}
                variant="outlined"
                size="small"
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
              >
                &lt;
              </Button>
            </Grid>
          </Grid>
          <Grid>{customList('Chosen', right)}</Grid>
        </Grid>
      )}
    </Fragment>
  );
}
